import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from '@mui/material';
import { CompareArrows, History } from '@mui/icons-material';
import { RecipeV2Detail, RecipeVersionComponentSnapshot } from '../types';
import RecipeV2EmptyState from './RecipeV2EmptyState';
import useRecipeVersions from '../hooks/useRecipeVersions';
import useRecipeGovernance from '../hooks/useRecipeGovernance';
import { formatUtcToLocalDateTime } from '../../../utils';

interface VersionsTabProps {
  recipe: RecipeV2Detail;
  canWrite: boolean;
  canApprove: boolean;
  canRollback: boolean;
  onGovernanceMutated: () => Promise<void>;
}

type DiffMode = 'sideBySide' | 'inline';

interface DiffEntry {
  description: string;
  type: 'added' | 'removed' | 'changed';
  oldValue?: string;
  newValue?: string;
}

const decisionColor = (decision: string | null): 'success' | 'warning' | 'info' | 'error' | 'default' => {
  const normalized = (decision ?? '').toLowerCase();
  if (normalized === 'approved') return 'success';
  if (normalized === 'submitted') return 'warning';
  if (normalized === 'rejected') return 'error';
  if (normalized === 'effective') return 'success';
  if (normalized === 'draft') return 'info';
  return 'default';
};

const decisionLabelKey = (decision: string | null): string => {
  const normalized = (decision ?? '').toLowerCase();
  if (normalized === 'approved' || normalized === 'effective') return 'versions.approved';
  if (normalized === 'submitted') return 'versions.pending';
  if (normalized === 'rejected') return 'versions.rejected';
  return 'versions.draft';
};

const componentIdentity = (component: RecipeVersionComponentSnapshot): string =>
  component.componentKey || `${component.componentItemId}-${component.id}`;

const componentLabel = (component: RecipeVersionComponentSnapshot): string =>
  `${component.componentItemId} (${component.componentRole})`;

const componentValue = (component: RecipeVersionComponentSnapshot): string =>
  `${component.quantity} ${component.uom}${component.weight != null ? ` (${component.weight}g)` : ''}`;

function computeDiff(
  oldComponents: RecipeVersionComponentSnapshot[],
  newComponents: RecipeVersionComponentSnapshot[],
): DiffEntry[] {
  const entries: DiffEntry[] = [];
  const oldMap = new Map(oldComponents.map((component) => [componentIdentity(component), component]));
  const newMap = new Map(newComponents.map((component) => [componentIdentity(component), component]));

  Array.from(newMap.entries()).forEach(([key, current]) => {
    const previous = oldMap.get(key);
    if (!previous) {
      entries.push({
        description: componentLabel(current),
        type: 'added',
        newValue: componentValue(current),
      });
      return;
    }

    if (
      previous.quantity !== current.quantity
      || previous.uom !== current.uom
      || previous.weight !== current.weight
      || previous.volume !== current.volume
      || previous.nestedRecipeVersionId !== current.nestedRecipeVersionId
    ) {
      entries.push({
        description: componentLabel(current),
        type: 'changed',
        oldValue: componentValue(previous),
        newValue: componentValue(current),
      });
    }
  });

  Array.from(oldMap.entries()).forEach(([key, previous]) => {
    if (!newMap.has(key)) {
      entries.push({
        description: componentLabel(previous),
        type: 'removed',
        oldValue: componentValue(previous),
      });
    }
  });

  return entries;
}

const diffColor: Record<string, string> = {
  added: 'success.light',
  removed: 'error.light',
  changed: 'warning.light',
};

const VersionsTab = ({
  recipe,
  canWrite,
  canApprove: canApprovePermission,
  canRollback: canRollbackPermission,
  onGovernanceMutated,
}: VersionsTabProps) => {
  const { t } = useTranslation('recipeV2');
  const [diffMode, setDiffMode] = useState<DiffMode>('sideBySide');
  const {
    versions,
    isLoading,
    loadError,
    refreshVersions,
    compareDialogOpen,
    setCompareDialogOpen,
    selectedVersions,
    detailByVersion,
    isCompareLoading,
    compareError,
    openComparison,
  } = useRecipeVersions(recipe.id);

  const governance = useRecipeGovernance({
    recipeId: recipe.id,
    refreshVersions,
    onGovernanceMutated,
    decisionReasonRequiredError: t('approval.commentRequired'),
  });

  const diffEntries = useMemo(() => {
    const [fromVersion, toVersion] = selectedVersions;
    if (fromVersion == null || toVersion == null) return [];
    const previous = detailByVersion[fromVersion]?.components ?? [];
    const current = detailByVersion[toVersion]?.components ?? [];
    return computeDiff(previous, current);
  }, [selectedVersions, detailByVersion]);

  const currentVersion = useMemo(() => versions.find((version) => version.versionNumber === recipe.currentVersion) ?? null, [versions, recipe.currentVersion]);

  const canSubmit = Boolean(currentVersion && canWrite);
  const canApprove = Boolean(currentVersion && canApprovePermission);
  const canReject = Boolean(currentVersion && canApprovePermission);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>
          <History sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('versions.title')}
        </Typography>
        {isLoading && (
          <Typography color="text.secondary">{t('loading')}</Typography>
        )}
        {loadError && (
          <Typography color="error">{loadError}</Typography>
        )}
        {!isLoading && !loadError && (
          <Stack spacing={1}>
            {governance.feedback && (
              <Typography color="success.main">
                {governance.feedback.startsWith('rollback:')
                  ? `Rollback created draft v${governance.feedback.split(':')[1]}.`
                  : t(`actions.${governance.feedback}`)}
              </Typography>
            )}
            {governance.error && (
              <Typography color="error">{governance.error}</Typography>
            )}
            {(canSubmit || canApprove || canReject) && (
              <Stack
                direction="row"
                spacing={1}
              >
                {canSubmit && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => governance.open('submit')}
                  >
                    {t('actions.submit')}
                  </Button>
                )}
                {canApprove && (
                  <Button variant="primary"
                    size="md"
                    onClick={() => governance.open('approve')}>
                    {t('actions.approve')}
                  </Button>
                )}
                {canReject && (
                  <Button variant="secondary"
                    size="md"
                    onClick={() => governance.open('reject')}>
                    {t('actions.reject')}
                  </Button>
                )}
              </Stack>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('version')}</TableCell>
                    <TableCell>{t('versions.description')}</TableCell>
                    <TableCell>{t('versions.changedBy')}</TableCell>
                    <TableCell>{t('versions.changedAt')}</TableCell>
                    <TableCell>{t('versions.approvalStatus')}</TableCell>
                    <TableCell>{t('versions.approvedBy')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versions.length === 0 ? (
                    <RecipeV2EmptyState colSpan={7}>{t('versions.empty', 'No versions available.')}</RecipeV2EmptyState>
                  ) : versions.map((version, idx) => (
                    <TableRow
                      key={version.id}
                      sx={{
                        bgcolor: version.versionNumber === recipe.currentVersion ? 'action.selected' : undefined,
                      }}
                    >
                      <TableCell>
                        <Stack direction="row"
                          alignItems="center"
                          spacing={1}>
                          <Typography fontWeight={600}>v{version.versionNumber}</Typography>
                          {version.versionNumber === recipe.currentVersion && (
                            <Chip label={t('versions.current')}
                              size="small"
                              color="primary" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{version.changeReason ?? '—'}</TableCell>
                      <TableCell>{version.createdBy ?? '—'}</TableCell>
                      <TableCell>{formatUtcToLocalDateTime(version.createdDate)}</TableCell>
                      <TableCell>
                        <Chip
                          label={t(decisionLabelKey(version.latestDecision))}
                          size="small"
                          color={decisionColor(version.latestDecision)}
                        />
                      </TableCell>
                      <TableCell>{version.decisionBy ?? '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}>
                          {idx < versions.length - 1 && (
                            <Button size="md"
                              startIcon={<CompareArrows />}
                              variant="secondary"
                              onClick={() => openComparison(
                                versions[idx + 1].versionNumber,
                                version.versionNumber,
                              )}>
                              {t('versions.compare')}
                            </Button>
                          )}
                          {canRollbackPermission && version.versionNumber !== recipe.currentVersion && (
                            <Button size="md"
                              variant="secondary"
                              onClick={() => governance.open('rollback', version.versionNumber)}>
                              {t('versions.rollback')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </Paper>

      <Dialog open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>
          <Stack direction="row"
            alignItems="center"
            justifyContent="space-between">
            <Typography variant="textLG">
              {t('versions.compareTitle')} — v{selectedVersions[0]} → v{selectedVersions[1]}
            </Typography>
            <ToggleButtonGroup
              value={diffMode}
              exclusive
              onChange={(_, value) => value && setDiffMode(value)}
              size="small"
            >
              <ToggleButton value="sideBySide">{t('versions.sideBySide')}</ToggleButton>
              <ToggleButton value="inline">{t('versions.inline')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {isCompareLoading && (
            <Typography color="text.secondary">{t('loading')}</Typography>
          )}
          {compareError && (
            <Typography color="error">{compareError}</Typography>
          )}
          {!isCompareLoading && !compareError && diffEntries.length === 0 ? (
            <Typography color="text.secondary"
              sx={{ py: 3, textAlign: 'center' }}>
              {t('versions.noChanges')}
            </Typography>
          ) : !isCompareLoading && !compareError && diffMode === 'inline' ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('ingredient')}</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {diffEntries.map((entry, index) => (
                  <TableRow key={`${entry.description}-${index}`}
                    sx={{ bgcolor: diffColor[entry.type] }}>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(`versions.${entry.type}`)}
                        size="small"
                        color={entry.type === 'added' ? 'success' : entry.type === 'removed' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {entry.type === 'changed' && (
                        <Typography variant="textSM">
                          {entry.oldValue} → {entry.newValue}
                        </Typography>
                      )}
                      {entry.type === 'added' && <Typography variant="textSM">{entry.newValue}</Typography>}
                      {entry.type === 'removed' && <Typography variant="textSM"
                        sx={{ textDecoration: 'line-through' }}>{entry.oldValue}</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : !isCompareLoading && !compareError ? (
            <Stack direction="row"
              spacing={2}>
              <Box flex={1}>
                <Typography variant="textSM"
                  fontWeight={600}
                  gutterBottom>v{selectedVersions[0]} (Previous)</Typography>
                <Table size="small">
                  <TableBody>
                    {diffEntries.map((entry, index) => (
                      <TableRow key={`${entry.description}-left-${index}`}
                        sx={{ bgcolor: entry.type === 'removed' ? 'error.light' : entry.type === 'changed' ? 'warning.light' : undefined }}>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell>{entry.oldValue ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Box flex={1}>
                <Typography variant="textSM"
                  fontWeight={600}
                  gutterBottom>v{selectedVersions[1]} (Current)</Typography>
                <Table size="small">
                  <TableBody>
                    {diffEntries.map((entry, index) => (
                      <TableRow key={`${entry.description}-right-${index}`}
                        sx={{ bgcolor: entry.type === 'added' ? 'success.light' : entry.type === 'changed' ? 'warning.light' : undefined }}>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell>{entry.newValue ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}
            variant="secondary">{t('actions.cancel')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={governance.action !== null}
        onClose={governance.close}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>
          {governance.action === 'submit'
            ? t('actions.submit')
            : governance.action === 'approve'
              ? t('actions.approve')
              : governance.action === 'reject'
                ? t('actions.reject')
                : t('versions.rollback')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ pt: 1 }}>
            {governance.action === 'rollback' && (
              <Typography>{t('versions.rollbackConfirm')}</Typography>
            )}
            <TextField
              label={t('approval.comment')}
              placeholder={t('approval.commentPlaceholder')}
              multiline
              minRows={3}
              value={governance.reason}
              onChange={(event) => governance.setReason(event.target.value)}
              disabled={governance.isBusy}
            />
            {governance.error && (
              <Typography color="error">{governance.error}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={governance.close}
            disabled={governance.isBusy}>
            {t('actions.cancel')}
          </Button>
          <Button variant="primary"
            onClick={governance.run}
            disabled={governance.isBusy}>
            {governance.action === 'submit'
              ? t('actions.submit')
              : governance.action === 'approve'
                ? t('approval.approveConfirm')
                : governance.action === 'reject'
                  ? t('approval.rejectConfirm')
                  : t('versions.rollback')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default VersionsTab;
